// CTV-638 — "which business is this campaign for?"
//
// Opened by AuthProvider when a buyer signs in mid-wizard with a business name
// that doesn't match any of the seats their account already has. Before this
// existed, the wizard silently discarded the typed business and attached the
// campaign to their pre-existing advertiser (a campaign built for "Apple
// Garden" shipped as an "RI Breakers" campaign).
//
// Rendered from AuthProvider and portaled to <body>, NOT from inside the
// wizard: the wizard body is keyed by identity, so the very sign-in that opens
// this modal remounts (and would destroy) anything the wizard owns.
//
// Deliberately not dismissable — no X, no escape, no backdrop click. The wizard
// cannot proceed without an answer, and every route out of the question is one
// of the three buttons below.

import React from 'react';
import { createPortal } from 'react-dom';
import { Building2, Loader2, Plus } from 'lucide-react';
import { apiClient, ApiError } from '../../../lib/apiClient';
import { usePublisherConfig } from '../../../data/publisherConfigs';
import PrimaryButton from '../shared/PrimaryButton';
import type { AdvertiserMembership } from './useAuth';
import { saveWizardContact, type PendingBusinessDecision } from './businessDecision';

// Sentinel for the "create the business I typed" radio option. Every other
// option value is an advertiserId, so this can't collide.
const CREATE_NEW = '__create_new__';

interface BusinessDecisionModalProps {
  decision: PendingBusinessDecision;
  /** Inject the freshly-created seat into useAuth's memberships list. */
  onSeatCreated: (membership: AdvertiserMembership) => void;
  /** Select the seat, close the modal, and release the wizard. */
  onResolved: (advertiserId: string) => void;
  /** Close the modal and send the buyer back to step 1 to fix the name. */
  onDeferred: () => void;
}

const BusinessDecisionModal: React.FC<BusinessDecisionModalProps> = ({
  decision,
  onSeatCreated,
  onResolved,
  onDeferred,
}) => {
  const { config } = usePublisherConfig(decision.slug);
  const accent = config?.accentColor || config?.primaryColor;
  const primary = config?.primaryColor;

  // Creating the business they typed is pre-selected: it's what they asked for
  // by typing it. Picking an existing seat is the deliberate override.
  const [choice, setChoice] = React.useState<string>(CREATE_NEW);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleContinue = async () => {
    setBusy(true);
    setError(null);
    try {
      let advertiserId = choice;
      if (choice === CREATE_NEW) {
        // Creation stays behind authentication: ensure-self only ever creates a
        // seat for the caller, so an outsider still can't attach a business to
        // someone else's account — they'd have to pass the OTP first.
        const resp = await apiClient.post<{
          membership: AdvertiserMembership;
          alreadyMember: boolean;
        }>('/memberships/ensure-self', {
          slug: decision.slug,
          businessName: decision.businessName.trim(),
          businessWebsite: decision.businessWebsite.trim() || null,
        });
        onSeatCreated(resp.membership);
        advertiserId = resp.membership.advertiserId;
      }
      // Contact name + phone are per-user, and the wizard's values win on both
      // branches (see saveWizardContact). Best-effort — never blocks the answer.
      await saveWizardContact({
        contactName: decision.contactName,
        contactPhone: decision.contactPhone,
      });
      onResolved(advertiserId);
    } catch (e) {
      setError(
        e instanceof ApiError || e instanceof Error
          ? e.message
          : 'Something went wrong. Please try again.',
      );
      setBusy(false);
    }
  };

  const typedName = decision.businessName.trim();

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/50 z-50" />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">
            Which business is this campaign for?
          </h2>
          <p className="mt-1 text-[13px] text-slate-600">
            You're signed in as{' '}
            <span className="font-medium text-slate-900">{decision.email}</span>, which
            already has a business on file.
          </p>
        </div>

        <div className="p-5 space-y-2">
          <label
            className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
              choice === CREATE_NEW
                ? 'border-slate-900 bg-slate-50'
                : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            <input
              type="radio"
              name="business-decision"
              value={CREATE_NEW}
              checked={choice === CREATE_NEW}
              onChange={() => setChoice(CREATE_NEW)}
              disabled={busy}
              className="mt-0.5"
            />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900">
                <Plus className="w-3.5 h-3.5 shrink-0" />
                Create new business: “{typedName}”
              </span>
              <span className="block text-[12px] text-slate-500 mt-0.5">
                {decision.businessWebsite.trim() || 'Adds it to your account.'}
              </span>
            </span>
          </label>

          {decision.memberships.map((m) => (
            <label
              key={m.advertiserId}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                choice === m.advertiserId
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name="business-decision"
                value={m.advertiserId}
                checked={choice === m.advertiserId}
                onChange={() => setChoice(m.advertiserId)}
                disabled={busy}
                className="mt-0.5"
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900">
                  <Building2 className="w-3.5 h-3.5 shrink-0" />
                  Use {m.advertiserName}
                </span>
                {m.businessWebsite && (
                  <span className="block text-[12px] text-slate-500 mt-0.5 truncate">
                    {m.businessWebsite}
                  </span>
                )}
              </span>
            </label>
          ))}

          {error && <p className="text-xs text-red-600 pt-1">{error}</p>}

          <div className="pt-2">
            <PrimaryButton
              type="button"
              onClick={handleContinue}
              disabled={busy}
              accentColor={accent}
              primaryColor={primary}
              fullWidth
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Continue
            </PrimaryButton>
          </div>

          <button
            type="button"
            onClick={onDeferred}
            disabled={busy}
            className="w-full py-2 text-[12px] text-slate-500 hover:text-slate-900 disabled:opacity-50"
          >
            Go back and edit the business name
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default BusinessDecisionModal;
